import { useState, useEffect } from "react";
import { Upload, Trash2, Image } from "lucide-react";
import { API_BASE_URL } from "../../api/apiClient";
import { JWT } from "../../config/constants";
import { showError, showSuccess } from "../../utils/toast";
import { showConfirm } from "../../utils/confirm";

export default function ClientLogoUpload({ projectId }) {
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadLogo();
    }
  }, [projectId]);

  async function loadLogo() {
    setLoading(true);
    try {
      const token = localStorage.getItem(JWT.TOKEN_KEY);
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/logo`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("Load logo response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("Logo data:", data);
        setLogo(data);
      } else if (response.status === 404) {
        // Logo not found is okay
        console.log("No logo found for this project");
        setLogo(null);
      }
    } catch (err) {
      // Logo not found is okay
      console.log("Error loading logo:", err);
      setLogo(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(png|jpeg)/)) {
      showError("Format file harus PNG atau JPEG");
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError("Ukuran file maksimal 2MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const token = localStorage.getItem(JWT.TOKEN_KEY);

      if (!token) {
        throw new Error("Token tidak ditemukan. Silakan login ulang.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/logo`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.error || `Upload failed with status ${response.status}`;

        // Special handling for 401 Unauthorized
        if (response.status === 401) {
          showError(
            "Sesi Anda telah berakhir. Silakan logout dan login kembali.",
          );
          throw new Error("Token expired or invalid");
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("Upload success:", result);

      showSuccess("Logo berhasil diupload");
      await loadLogo(); // Reload logo after success
    } catch (err) {
      console.error("Upload error:", err);
      showError(err.message || "Gagal upload logo. Silakan coba lagi.");
    } finally {
      setUploading(false);
      // Reset input
      if (e.target) {
        e.target.value = "";
      }
    }
  }

  async function handleDelete() {
    showConfirm({
      title: "Konfirmasi Hapus Logo",
      message: "Apakah Anda yakin ingin menghapus logo client?",
      confirmLabel: "Ya, Hapus",
      cancelLabel: "Batal",
      onConfirm: async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem(JWT.TOKEN_KEY);
          const response = await fetch(
            `${API_BASE_URL}/api/projects/${projectId}/logo`,
            {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (!response.ok) {
            throw new Error("Delete failed");
          }

          showSuccess("Logo berhasil dihapus");
          setLogo(null);
        } catch (err) {
          console.error("Delete error:", err);
          showError("Gagal menghapus logo");
        } finally {
          setLoading(false);
        }
      },
    });
    // if (!window.confirm("Hapus logo client?")) return;
  }

  if (!projectId) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold mb-4">
        Logo Client (untuk Laporan Excel)
      </h3>

      <div className="flex flex-col items-center gap-4">
        {/* Logo Preview */}
        <div className="w-full max-w-xs h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
          {logo && logo.logo_path ? (
            <img
              src={`${API_BASE_URL}/${logo.logo_path}?t=${Date.now()}`}
              alt="Client Logo"
              className="max-h-full max-w-full object-contain p-2"
              onError={(e) => {
                console.error("Failed to load logo image");
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="text-center text-gray-400">
              <Image size={48} className="mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm font-medium">Belum ada logo</p>
              <p className="text-xs mt-1">Upload logo client Anda</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            <Upload size={18} />
            <span>
              {uploading ? "Uploading..." : logo ? "Ganti Logo" : "Upload Logo"}
            </span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>

          {logo && (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
            >
              <Trash2 size={18} />
              <span>Hapus</span>
            </button>
          )}
        </div>

        {/* Info */}
        <div className="text-sm text-gray-500 text-center">
          <p>Format: PNG atau JPEG</p>
          <p>Ukuran maksimal: 2MB</p>
          <p>Resolusi rekomendasi: 200x100px (ratio 2:1)</p>
        </div>
      </div>
    </div>
  );
}
